/**
 * Structural rules for the Adversarial Robustness Red-Team game.
 *
 * These functions ARE the RD-31 theorems, encoded as a legal-move generator
 * and the structural predicates the generator rests on. They are pure and
 * React-free, so the structural invariants can be proved directly in
 * tests/rules.test.ts.
 *
 * The central discipline (skill-74): the forbidden honest-to-honest square is
 * ABSENT from `legalSquares` — it is structurally inexpressible, not rejected
 * after the fact. The Red Team is structurally confined to squares that factor
 * through at least one compromised agent (RD-31 §4.3 violation confinement).
 *
 * Companion to research/settled/RD-31-ADVERSARIAL-MULTI-AGENT-ROBUSTNESS.md §4.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import type { AgentId, Scenario, SquareCandidate } from "./types.js";

/** Stable key for an unordered agent pair. */
export function pairKey(a: AgentId, b: AgentId): string {
  return a < b ? `${a}::${b}` : `${b}::${a}`;
}

/**
 * Whether the scenario declares a governed interface between a and b. The
 * interface is undirected; honest agents validate squares at this boundary.
 */
export function hasGovernedInterface(
  scenario: Scenario,
  a: AgentId,
  b: AgentId,
): boolean {
  const key = pairKey(a, b);
  return scenario.interfaces.some((i) => pairKey(i.a, i.b) === key);
}

/**
 * A square between two HONEST agents through their governed interface.
 *
 * This is the construction the Red Team would most like to realize — and it
 * is the one the legal-move generator must never offer. The honest-agent
 * isolation lemma (RD-31 §4.2), which rests on RD-05's mechanized
 * compositional-safety, makes this composite structurally inexpressible.
 */
export function isHonestHonest(
  compromised: ReadonlySet<AgentId>,
  a: AgentId,
  b: AgentId,
): boolean {
  return !compromised.has(a) && !compromised.has(b);
}

/**
 * The compromised agents a square between a and b factors through. For a
 * direct cross-agent square the factors are exactly the endpoints that are
 * compromised. This is the witness for violation confinement: the list is
 * non-empty for every realizable forbidden square.
 */
export function factorsThroughAdversary(
  compromised: ReadonlySet<AgentId>,
  a: AgentId,
  b: AgentId,
): readonly AgentId[] {
  const factors: AgentId[] = [];
  if (compromised.has(a)) factors.push(a);
  if (compromised.has(b)) factors.push(b);
  return factors;
}

/**
 * The legal forbidden-square candidates the Red Team may attempt, given the
 * current compromise set. This is the move GENERATOR: a candidate appears iff
 * it factors through at least one compromised agent across a governed
 * interface. The honest-to-honest square is, by construction, never produced.
 *
 * Determinism: candidates are emitted in a fixed order (interface declaration
 * order), so the generator is reproducible for a fixed scenario.
 */
export function legalSquares(
  scenario: Scenario,
  compromised: ReadonlySet<AgentId>,
): readonly SquareCandidate[] {
  const out: SquareCandidate[] = [];
  for (const iface of scenario.interfaces) {
    const factors = factorsThroughAdversary(compromised, iface.a, iface.b);
    if (factors.length === 0) {
      // Honest-to-honest through a governed interface: unconstructable.
      continue;
    }
    out.push({ a: iface.a, b: iface.b, factorsThrough: factors });
  }
  return out;
}

/** Whether a specific pair is currently a legal (offerable) square. */
export function isLegalSquare(
  scenario: Scenario,
  compromised: ReadonlySet<AgentId>,
  a: AgentId,
  b: AgentId,
): boolean {
  const key = pairKey(a, b);
  return legalSquares(scenario, compromised).some(
    (s) => pairKey(s.a, s.b) === key,
  );
}

/**
 * The full reachable VIOLATION SURFACE: every forbidden square the Red Team
 * could realize under the current compromise set. It is exactly the legal
 * square set — the player is structurally confined to it (RD-31 §4.3).
 */
export function violationSurface(
  scenario: Scenario,
  compromised: ReadonlySet<AgentId>,
): readonly SquareCandidate[] {
  return legalSquares(scenario, compromised);
}

/**
 * The quantitative violation-surface bound from RD-31 §4.4:
 *
 *   |CLOSE(⋃ ALLOWᵢ) ∩ FORBID| ≤ k·a³ + k²·a²·A
 *
 * where k is the number of compromised agents, a is the maximum adversary
 * action space, and A is the total honest action space. This is a CONJECTURED
 * quantitative bound (RD-31 §5); we display it as a HUD ceiling that the
 * realized surface provably never exceeds.
 */
export function surfaceBound(
  scenario: Scenario,
  compromised: ReadonlySet<AgentId>,
): number {
  const k = compromised.size;
  if (k === 0) return 0;
  let a = 0;
  let honestTotal = 0;
  for (const agent of scenario.agents) {
    if (compromised.has(agent.id)) {
      a = Math.max(a, agent.actionSpace);
    } else {
      honestTotal += agent.actionSpace;
    }
  }
  return k * a * a * a + k * k * a * a * honestTotal;
}

/**
 * The size of the realized surface relative to the reachable surface — the
 * coverage score. The player "wins the argument" not by blocking threats but
 * by exploring how much of the (bounded) adversary-reachable surface exists.
 * When the reachable surface is empty (k = 0) coverage is defined as 0.
 */
export function coverage(reachable: number, realized: number): number {
  if (reachable <= 0) return 0;
  return Math.min(1, realized / reachable);
}
